/**
 * Copy text to the clipboard, including over plain HTTP.
 *
 * *** THE MODERN API IS UNAVAILABLE ON THIS DEPLOYMENT ***
 * `navigator.clipboard` is gated behind a SECURE CONTEXT — https, or localhost.
 * This portal is served over plain http on a LAN address, so `navigator.
 * clipboard` is simply `undefined` there and the modern path can never run.
 * That is why every "Copy" click reported failure: there was no fallback, only
 * a message telling the admin to select the text by hand.
 *
 * It matters most for exactly the values that cannot be recovered — a mentor's
 * one-time password is shown once and is unreadable afterwards, so a copy
 * button that does nothing risks losing it.
 *
 * The fallback is `document.execCommand('copy')`. It is deprecated, and it is
 * also the only thing that works in an insecure context; browsers keep it for
 * this reason. Remove it once the portal is behind HTTPS.
 */
export async function copyText(text: string): Promise<boolean> {
  if (window.isSecureContext && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // Permission denied, or a browser that refuses without a user gesture.
      // Fall through rather than giving up — the legacy path often still works.
    }
  }
  return legacyCopy(text)
}

function legacyCopy(text: string): boolean {
  const area = document.createElement('textarea')
  area.value = text
  area.setAttribute('readonly', '')

  /*
   * Positioned off-screen rather than hidden: `display: none` and
   * `visibility: hidden` both make the element unselectable, and a selection is
   * exactly what execCommand copies. `position: fixed` keeps it from scrolling
   * the page as it is focused.
   */
  area.style.position = 'fixed'
  area.style.top = '0'
  area.style.left = '-9999px'
  area.style.opacity = '0'

  /*
   * *** IT MUST LIVE INSIDE THE OPEN DIALOG ***
   * `showModal()` makes everything outside the dialog inert. A textarea
   * appended to <body> while a modal is open cannot take focus, so the
   * selection is empty and the copy silently copies nothing — which is
   * precisely where these buttons are used.
   */
  const host = document.querySelector('dialog[open]') ?? document.body
  host.appendChild(area)

  try {
    area.focus()
    area.select()
    // iOS ignores select() on a readonly field; setSelectionRange is what
    // actually establishes the range there.
    area.setSelectionRange(0, text.length)
    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    area.remove()
  }
}
